'use client'

import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from 'react'
import { Dialog } from '@acko/dialog'
import { pageEvent, trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import {
  ABHA_OTP_LENGTH,
  type AbhaCardPreviewData,
  isValidAadhaarNumber,
  sanitizeAadhaar,
} from '@helpers/abhaWidgetHelper'
import { sendAbhaAadhaarOtp, verifyAbhaAadhaarOtp } from '@services/abhaServices/abhaAadhaarOtp'
import {
  AbhaFlowError,
  advanceAbhaNextStep,
  buildCardPreviewFromSession,
  buildMemberListWithAadhaar,
  buildMemberListWithOtp,
  buildMemberListWithPhone,
  getPrefillAadhaarFromSearchParams,
  isAbhaOtpInvalidError,
  resolveModalStepFromNodeId,
  resolveMaskedPhoneNumberFromSession,
  sanitizeAbhaErrorMessage,
  shouldUseMockAbhaFlow,
  startAbhaHeroJourney,
  type AbhaSessionState,
} from '@helpers/abhaJourneyHelper'
import { isValidPhoneNumber } from '@utils/utils'
import logger from '@utils/logger'
import { getClassNames } from '@helpers/productWidgetHelper'
import {
  ABHA_HERO_AADHAAR_ERROR_MESSAGE,
  ABHA_HERO_AADHAAR_VERIFICATION_DELAY_ERROR_MESSAGE,
  ABHA_HERO_MODAL_AADHAAR_STEP_DATA,
  ABHA_HERO_MODAL_RESEND_OTP_SECONDS,
  ABHA_HERO_OTP_ERROR_MESSAGE,
  ABHA_HERO_PHONE_ERROR_MESSAGE,
  ABHA_HERO_SUCCESS_APP_LINK,
  DEFAULT_ABHA_CARD_PREVIEW,
} from '@components/NewProduct/constants'
import { sanitizeAbhaPhoneNumber } from '@helpers/abhaPhoneHelper'
import { AbhaHeroModalStep, AbhaJourneyNodeId } from '@components/NewProduct/AbhaHeroWidget/enums'
import { ABHA_SEO_WIDGET } from '@components/Product/ProductWidget/constants'
import { fetchDeepLink } from '@services/fetchDeepLink'
import {
  DEFAULT_ABHA_SUCCESS_CARD_VARIANT,
  resolveAbhaSuccessCardVariant,
} from '@components/NewProduct/AbhaHeroWidget/steps/abhaSuccessTypes'
import { useBodyScrollLock } from '@components/NewProduct/AbhaHeroWidget/useBodyScrollLock'
import {
  ABHA_ANALYTICS_ACTION_TYPES,
  ABHA_ANALYTICS_EVENTS,
  ABHA_ANALYTICS_JOURNEYS,
  ABHA_ANALYTICS_PAGE_TYPES,
  ABHA_STATE,
  type AbhaAnalyticsContext,
  buildAbhaAnalyticsProps,
  buildAbhaPageViewProps,
  resolveAbhaSuccessVariantName,
} from '@components/NewProduct/AbhaHeroWidget/abhaHeroAnalytics'
import { downloadAbhaCardAsset } from '@components/NewProduct/AbhaHeroWidget/abhaCardDownload'
import { AbhaHeroModalCloseButton } from '@components/NewProduct/AbhaHeroWidget/AbhaHeroModalCloseButton'
import './abhaHeroModal.css'
import './abhaHeroSuccess.css'
import { AbhaHeroAadhaarStep } from './steps/AbhaHeroAadhaarStep'
import { AbhaHeroPrepareStep } from './steps/AbhaHeroPrepareStep'
import { AbhaHeroOtpStep } from './steps/AbhaHeroOtpStep'
import { AbhaHeroSuccessStep } from './steps/AbhaHeroSuccessStep'
import { AbhaHeroErrorStep } from './steps/AbhaHeroErrorStep'

export type AbhaHeroModalProps = {
  open: boolean
  phone: string
  initialStep: AbhaHeroModalStep
  abhaSession: AbhaSessionState | null
  modalOptions: any
  searchParams?: Record<string, string>
  segmentEventCommonProperties?: Record<string, unknown>
  previewMode?: boolean
  previewErrorMessage?: string
  previewViewport?: 'mobile' | 'desktop'
  onClose: () => void
  onCreationComplete?: () => void
  /** When true, phone is collected in the Aadhaar step and the transaction API runs on Send OTP */
  collectPhoneInAadhaarStep?: boolean
  analyticsContext?: AbhaAnalyticsContext
}

const MOBILE_BREAKPOINT = 1024
const EMPTY_SEGMENT_EVENT_PROPERTIES: Record<string, unknown> = {}

const isMobileOtpNode = (nodeId?: string) => nodeId === AbhaJourneyNodeId.MobileOtpVerificationV2

export const AbhaHeroModal = ({
  open,
  phone,
  initialStep,
  abhaSession,
  modalOptions = {},
  searchParams = {},
  segmentEventCommonProperties = EMPTY_SEGMENT_EVENT_PROPERTIES,
  previewMode = false,
  previewErrorMessage,
  previewViewport,
  onClose,
  onCreationComplete,
  collectPhoneInAadhaarStep = false,
  analyticsContext = 'widget',
}: AbhaHeroModalProps) => {
  const prefilledAadhaar = getPrefillAadhaarFromSearchParams(searchParams)
  const hasManualAadhaarEdit = useRef(false)
  const firedPageEventsRef = useRef<Set<string>>(new Set())
  const [step, setStep] = useState<AbhaHeroModalStep>(initialStep)
  const [session, setSession] = useState<AbhaSessionState | null>(abhaSession)
  const [isMobile, setIsMobile] = useState(false)
  const [aadhaar, setAadhaar] = useState(prefilledAadhaar)
  const [aadhaarError, setAadhaarError] = useState('')
  const [hasConsent, setHasConsent] = useState(false)
  const [otp, setOtp] = useState('')
  const [otpError, setOtpError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [prepareError, setPrepareError] = useState('')
  const [appDownloadLink, setAppDownloadLink] = useState(ABHA_HERO_SUCCESS_APP_LINK)
  const [cardPreview, setCardPreview] = useState<AbhaCardPreviewData>(DEFAULT_ABHA_CARD_PREVIEW)
  const [resendCountdown, setResendCountdown] = useState(ABHA_HERO_MODAL_RESEND_OTP_SECONDS)
  const [modalPhone, setModalPhone] = useState('')
  const [modalPhoneError, setModalPhoneError] = useState('')
  const [flowPhone, setFlowPhone] = useState(phone)

  const getActivePhone = useCallback(
    () => (collectPhoneInAadhaarStep ? sanitizeAbhaPhoneNumber(modalPhone) || flowPhone : phone),
    [collectPhoneInAadhaarStep, flowPhone, modalPhone, phone]
  )

  const otpMaskedPhoneNumber = useMemo(() => {
    const fromSession = resolveMaskedPhoneNumberFromSession(session)

    if (fromSession) {
      return fromSession
    }

    const activePhone = getActivePhone()

    if (activePhone.length >= 4) {
      return `******${activePhone.slice(-4)}`
    }

    return undefined
  }, [getActivePhone, session])
  const isMobileOtpStep = isMobileOtpNode(session?.next_node_id) || isMobileOtpNode(session?.current_node_id)
  const isNewAbhaCreateAnalytics = analyticsContext === 'new_abha_create'
  const successVariantName = resolveAbhaSuccessVariantName(modalOptions?.successStepData)
  const successCtaAnalyticsProps =
    !previewMode && successVariantName === 'hospital_bill'
      ? buildAbhaAnalyticsProps({
          action_type: ABHA_ANALYTICS_ACTION_TYPES.REDIRECTION,
          cta_text: '',
          current_state: ABHA_STATE.DOWNLOAD_ABHA,
          journey: ABHA_ANALYTICS_JOURNEYS.NEW_ABHA_ENTRY,
          page_type:
            modalOptions?.successStepData?.pageType ??
            modalOptions?.pageType ??
            segmentEventCommonProperties?.page_type ??
            'seo',
          platform: isMobile ? 'mweb' : 'web',
          variant_name: successVariantName,
        })
      : undefined

  const getNewAbhaCreateInteractionProps = useCallback(() => {
    const {
      page,
      type,
      product,
      family_member: familyMember,
      communication_phone: communicationPhone,
    } = segmentEventCommonProperties

    return {
      page,
      type,
      product,
      family_member: familyMember,
      communication_phone: communicationPhone,
    }
  }, [segmentEventCommonProperties])

  const showAbhaError = useCallback(
    (error: unknown, activeSession?: AbhaSessionState | null) => {
      setErrorMessage(sanitizeAbhaErrorMessage(error, activeSession ?? session ?? undefined))
      setStep(AbhaHeroModalStep.Error)
    },
    [session]
  )

  const routeToNextStep = useCallback(
    async (nextSession: AbhaSessionState) => {
      let resolvedSession = nextSession

      if (resolvedSession.next_node_id === AbhaJourneyNodeId.MobileLinkingV2) {
        const { session: mobileOtpSession } = await advanceAbhaNextStep(
          resolvedSession,
          AbhaJourneyNodeId.MobileLinkingV2,
          buildMemberListWithPhone(resolvedSession.abha_member_list_details, getActivePhone())
        )

        resolvedSession = mobileOtpSession
      }

      const nextStep = resolveModalStepFromNodeId(resolvedSession.next_node_id)

      if (nextStep === AbhaHeroModalStep.Error) {
        showAbhaError(null, resolvedSession)
        return
      }

      setSession(resolvedSession)

      if (nextStep === AbhaHeroModalStep.Success || nextStep === AbhaHeroModalStep.Prepare) {
        setCardPreview(buildCardPreviewFromSession(resolvedSession))
      }

      if (nextStep === AbhaHeroModalStep.Otp) {
        setOtp('')
        setOtpError('')
        setSubmitError('')
      }

      setStep(nextStep)
    },
    [getActivePhone, showAbhaError]
  )

  const resetModalState = useCallback(() => {
    setStep(initialStep)
    setSession(abhaSession)
    setAadhaar(prefilledAadhaar)
    setAadhaarError('')
    setHasConsent(false)
    setOtp('')
    setOtpError('')
    setSubmitError('')
    setErrorMessage('')
    setPrepareError('')
    setAppDownloadLink(ABHA_HERO_SUCCESS_APP_LINK)
    setCardPreview(DEFAULT_ABHA_CARD_PREVIEW)
    setIsSubmitting(false)
    setResendCountdown(ABHA_HERO_MODAL_RESEND_OTP_SECONDS)
    setModalPhone('')
    setModalPhoneError('')
    setFlowPhone(phone)
  }, [abhaSession, initialStep, phone, prefilledAadhaar])

  const handleClose = useCallback(() => {
    hasManualAadhaarEdit.current = false
    resetModalState()
    onClose()
  }, [onClose, resetModalState])

  useEffect(() => {
    if (!open) return

    setStep(initialStep)
    setSession(abhaSession)

    if (abhaSession) {
      setCardPreview(buildCardPreviewFromSession(abhaSession))
    }

    if (previewMode && previewErrorMessage) {
      setErrorMessage(previewErrorMessage)
    }
  }, [abhaSession, initialStep, open, previewErrorMessage, previewMode])

  useEffect(() => {
    if (previewMode && previewViewport) {
      setIsMobile(previewViewport === 'mobile')
      return
    }

    const checkViewport = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)

    checkViewport()
    window.addEventListener('resize', checkViewport)

    return () => window.removeEventListener('resize', checkViewport)
  }, [previewMode, previewViewport])

  useBodyScrollLock(open)

  useEffect(() => {
    if (!open || previewMode) {
      firedPageEventsRef.current.clear()
      return
    }

    if (isNewAbhaCreateAnalytics) {
      if (step === AbhaHeroModalStep.Aadhaar) {
        const eventName = ABHA_ANALYTICS_EVENTS.VIEW_AADHAAR_ABHA_PAGE

        if (firedPageEventsRef.current.has(eventName)) return

        firedPageEventsRef.current.add(eventName)
        void pageEvent(eventName, {
          name: eventName,
          ...segmentEventCommonProperties,
          journey: ABHA_ANALYTICS_JOURNEYS.CREATE_ABHA,
        })
        return
      }

      if (step === AbhaHeroModalStep.Otp) {
        const otpPage = isMobileOtpStep
          ? ABHA_ANALYTICS_PAGE_TYPES.PHONE_OTP_SCREEN
          : ABHA_ANALYTICS_PAGE_TYPES.AADHAAR_OTP_SCREEN

        if (firedPageEventsRef.current.has(otpPage)) return

        firedPageEventsRef.current.add(otpPage)
        void pageEvent(ABHA_ANALYTICS_EVENTS.APP_VIEW_PAGE, {
          ...segmentEventCommonProperties,
          page_type: otpPage,
          journey: ABHA_ANALYTICS_JOURNEYS.CREATE_ABHA,
        })
      }

      return
    }

    const successVariantName = resolveAbhaSuccessVariantName(modalOptions?.successStepData)

    const pageViewEvents: Array<{ key: string; journey: string; extra?: Record<string, unknown> }> = []

    if (step === AbhaHeroModalStep.Aadhaar) {
      pageViewEvents.push({
        key: ABHA_ANALYTICS_PAGE_TYPES.AADHAAR_PHONE_ENTRY,
        journey: ABHA_ANALYTICS_JOURNEYS.NEW_ABHA_ENTRY,
        extra: {
          component_name: ABHA_ANALYTICS_PAGE_TYPES.AADHAAR_PHONE_ENTRY,
        },
      })
    }

    if (step === AbhaHeroModalStep.Otp) {
      const pageType = isMobileOtpStep
        ? ABHA_ANALYTICS_PAGE_TYPES.PHONE_VERIFICATION_SCREEN
        : ABHA_ANALYTICS_PAGE_TYPES.AADHAAR_VERIFICATION_SCREEN

      pageViewEvents.push({
        key: pageType,
        journey: ABHA_ANALYTICS_JOURNEYS.NEW_ABHA_ENTRY,
        extra: {
          component_name: pageType,
        },
      })
    }

    if (step === AbhaHeroModalStep.Success) {
      const successData = modalOptions?.successStepData
      const analyticsPage = segmentEventCommonProperties?.page
      const currentState =
        resolveAbhaSuccessCardVariant(successData?.cardVariant ?? DEFAULT_ABHA_SUCCESS_CARD_VARIANT) ===
        'showAbhaNumber'
          ? ABHA_STATE.DOWNLOAD_ABHA
          : ABHA_STATE.VIEW_ABHA

      pageViewEvents.push({
        key: ABHA_ANALYTICS_JOURNEYS.APP_DOWNLOAD_BANNER,
        journey: ABHA_ANALYTICS_JOURNEYS.NEW_ABHA_ENTRY,
        extra: {
          ...(successVariantName ? { variant_name: successVariantName } : {}),
          component_name: ABHA_ANALYTICS_JOURNEYS.APP_DOWNLOAD_BANNER,
          current_state: currentState,
          page_type: successData?.pageType ?? modalOptions?.pageType ?? segmentEventCommonProperties?.page_type,
          page: typeof analyticsPage === 'string' && analyticsPage ? analyticsPage : window.location.href,
        },
      })
    }

    pageViewEvents.forEach(({ key, journey, extra }) => {
      if (firedPageEventsRef.current.has(key)) return

      firedPageEventsRef.current.add(key)
      void pageEvent(ABHA_ANALYTICS_EVENTS.VIEW_PAGE, buildAbhaPageViewProps(journey, extra))
    })
  }, [
    isMobileOtpStep,
    isNewAbhaCreateAnalytics,
    modalOptions?.pageType,
    modalOptions?.successStepData,
    open,
    previewMode,
    segmentEventCommonProperties,
    step,
  ])

  useEffect(() => {
    if (!open || collectPhoneInAadhaarStep) return

    setFlowPhone(phone)
  }, [collectPhoneInAadhaarStep, open, phone])

  useEffect(() => {
    if (!open || hasManualAadhaarEdit.current) return

    setAadhaar(prefilledAadhaar)
  }, [open, prefilledAadhaar])

  useEffect(() => {
    if (!open || step !== AbhaHeroModalStep.Otp) return undefined

    const handleEnterKey = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Enter' || isSubmitting || otp.length !== ABHA_OTP_LENGTH) return

      const target = event.target

      if (!(target instanceof HTMLElement) || target.tagName === 'TEXTAREA') return

      event.preventDefault()
      void handleVerifyOtp()
    }

    window.addEventListener('keydown', handleEnterKey)

    return () => window.removeEventListener('keydown', handleEnterKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting, open, otp, step])

  useEffect(() => {
    if (step !== AbhaHeroModalStep.Otp || !open) return undefined

    setResendCountdown(ABHA_HERO_MODAL_RESEND_OTP_SECONDS)

    const intervalId = window.setInterval(() => {
      setResendCountdown(previous => (previous <= 1 ? 0 : previous - 1))
    }, 1000)

    return () => window.clearInterval(intervalId)
  }, [open, step])

  const fetchAbhaAppDownloadLink = useCallback(async () => {
    if (previewMode || isNewAbhaCreateAnalytics) {
      return ''
    }

    return await fetchDeepLink({
      widgetType: ABHA_SEO_WIDGET,
      mobileNo: getActivePhone(),
    })
  }, [getActivePhone, isNewAbhaCreateAnalytics, previewMode])

  useEffect(() => {
    if (previewMode || step !== AbhaHeroModalStep.Prepare || !open || !session) return undefined

    let cancelled = false

    void (async () => {
      setPrepareError('')
      const appDownloadLinkPromise = fetchAbhaAppDownloadLink()

      const updateAppDownloadLink = async () => {
        const deepLink = await appDownloadLinkPromise

        if (!cancelled && deepLink) {
          setAppDownloadLink(deepLink)
        }
      }

      try {
        if (shouldUseMockAbhaFlow(session)) {
          await new Promise<void>(resolve => {
            setTimeout(resolve, 1500)
          })
          await updateAppDownloadLink()

          if (!cancelled) {
            if (onCreationComplete) {
              onCreationComplete()
              return
            }

            setStep(AbhaHeroModalStep.Success)
          }

          return
        }

        const { session: nextSession } = await advanceAbhaNextStep(
          session,
          AbhaJourneyNodeId.CreationV2,
          session.abha_member_list_details
        )
        await updateAppDownloadLink()

        if (!cancelled) {
          if (onCreationComplete) {
            onCreationComplete()
            return
          }

          setSession(nextSession)
          setCardPreview(buildCardPreviewFromSession(nextSession))
          setStep(AbhaHeroModalStep.Success)
        }
      } catch (error) {
        if (!cancelled) {
          if (isAbhaOtpInvalidError(error)) {
            setPrepareError(error.message)
            setStep(AbhaHeroModalStep.Otp)
            return
          }

          setPrepareError(sanitizeAbhaErrorMessage(error, session))
          showAbhaError(error, session)
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [fetchAbhaAppDownloadLink, onCreationComplete, open, phone, previewMode, session, showAbhaError, step])

  const handleAadhaarChange = (event: ChangeEvent<HTMLInputElement>) => {
    hasManualAadhaarEdit.current = true
    setAadhaar(sanitizeAadhaar(event.target.value))

    if (aadhaarError) {
      setAadhaarError('')
    }

    if (submitError) {
      setSubmitError('')
    }
  }

  const handleAadhaarBlur = () => {
    setAadhaarError(isValidAadhaarNumber(aadhaar) ? '' : ABHA_HERO_AADHAAR_ERROR_MESSAGE)
  }

  const handleModalPhoneChange = (event: ChangeEvent<HTMLInputElement>) => {
    setModalPhone(sanitizeAbhaPhoneNumber(event.target.value))

    if (modalPhoneError) {
      setModalPhoneError('')
    }

    if (submitError) {
      setSubmitError('')
    }
  }

  const handleModalPhoneBlur = () => {
    setModalPhoneError(isValidPhoneNumber(sanitizeAbhaPhoneNumber(modalPhone)) ? '' : ABHA_HERO_PHONE_ERROR_MESSAGE)
  }

  const handleSendOtp = async () => {
    if (previewMode || isSubmitting) return

    if (!collectPhoneInAadhaarStep && !session) return

    if (!isValidAadhaarNumber(aadhaar)) {
      setAadhaarError(ABHA_HERO_AADHAAR_ERROR_MESSAGE)
      return
    }

    if (!hasConsent) {
      setSubmitError('Please accept the consent to continue.')
      return
    }

    if (collectPhoneInAadhaarStep) {
      const sanitizedPhone = sanitizeAbhaPhoneNumber(modalPhone)

      if (!isValidPhoneNumber(sanitizedPhone)) {
        setModalPhoneError(ABHA_HERO_PHONE_ERROR_MESSAGE)
        return
      }

      setModalPhoneError('')
    }

    setIsSubmitting(true)
    setSubmitError('')

    try {
      let activeSession = session
      let activePhone = getActivePhone()

      if (collectPhoneInAadhaarStep && !activeSession) {
        const sanitizedPhone = sanitizeAbhaPhoneNumber(modalPhone)
        const { session: newSession } = await startAbhaHeroJourney({
          phone: sanitizedPhone,
          aadhaar,
          searchParams,
        })

        activeSession = newSession
        activePhone = sanitizedPhone
        setSession(newSession)
        setFlowPhone(sanitizedPhone)
      }

      if (!activeSession) {
        return
      }

      const aadhaarCtaText = modalOptions?.aadhaarStepData?.ctaText ?? ABHA_HERO_MODAL_AADHAAR_STEP_DATA.CTA_TEXT

      if (isNewAbhaCreateAnalytics) {
        await trackEvent(ABHA_ANALYTICS_EVENTS.TAP_AADHAAR_NUMBER_SUBMIT, getNewAbhaCreateInteractionProps())
      } else {
        await trackEvent(
          ABHA_ANALYTICS_EVENTS.TAP_BTN_CTA_WIDGET,
          buildAbhaAnalyticsProps({
            journey: ABHA_ANALYTICS_JOURNEYS.NEW_ABHA_ENTRY,
            cta_text: aadhaarCtaText,
            page_type: ABHA_ANALYTICS_PAGE_TYPES.AADHAAR_PHONE_ENTERED,
          })
        )
      }

      if (shouldUseMockAbhaFlow(activeSession)) {
        await sendAbhaAadhaarOtp({ phone: activePhone, aadhaar })
        setStep(AbhaHeroModalStep.Otp)
        setOtp('')
        setOtpError('')
        return
      }

      const updatedMembers = buildMemberListWithAadhaar(
        activeSession.abha_member_list_details,
        aadhaar,
        hasConsent,
        activePhone
      )
      const { session: nextSession } = await advanceAbhaNextStep(
        activeSession,
        AbhaJourneyNodeId.AadhaarInputV2,
        updatedMembers
      )

      await routeToNextStep(nextSession)
    } catch (error) {
      if (isAbhaOtpInvalidError(error)) {
        setStep(AbhaHeroModalStep.Otp)
        setOtpError(error.message)
        return
      }

      setSubmitError(sanitizeAbhaErrorMessage(error, session ?? undefined))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleResendOtp = async () => {
    if (previewMode || !session) return

    const activePhone = getActivePhone()

    void trackEvent(
      ABHA_ANALYTICS_EVENTS.TAP_BTN_ENGAGE,
      buildAbhaAnalyticsProps({
        journey: ABHA_ANALYTICS_JOURNEYS.NEW_ABHA_ENTRY,
        component_name: ABHA_ANALYTICS_ACTION_TYPES.RESEND_OTP,
      })
    )

    setIsSubmitting(true)
    setSubmitError('')
    setOtpError('')

    try {
      if (shouldUseMockAbhaFlow(session)) {
        await sendAbhaAadhaarOtp({ phone: activePhone, aadhaar })
        setResendCountdown(ABHA_HERO_MODAL_RESEND_OTP_SECONDS)

        return
      }

      const isMobileOtpVerificationStep =
        isMobileOtpNode(session.next_node_id) || isMobileOtpNode(session.current_node_id)
      const resendNodeId = isMobileOtpVerificationStep
        ? AbhaJourneyNodeId.MobileLinkingV2
        : AbhaJourneyNodeId.ResendOtpV2
      const resendMemberListDetails = isMobileOtpVerificationStep
        ? buildMemberListWithPhone(session.abha_member_list_details, activePhone)
        : session.abha_member_list_details
      const { session: nextSession } = await advanceAbhaNextStep(session, resendNodeId, resendMemberListDetails)

      setSession(nextSession)
      setResendCountdown(ABHA_HERO_MODAL_RESEND_OTP_SECONDS)
    } catch (error) {
      if (isAbhaOtpInvalidError(error)) {
        setOtpError(error.message)
        return
      }

      setSubmitError(sanitizeAbhaErrorMessage(error, session))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleVerifyOtp = async () => {
    if (previewMode || isSubmitting || !session) return

    const activePhone = getActivePhone()

    if (otp.length !== ABHA_OTP_LENGTH) {
      setOtpError(ABHA_HERO_OTP_ERROR_MESSAGE)
      return
    }

    setIsSubmitting(true)
    setSubmitError('')
    setOtpError('')

    const isMobileOtpVerificationStep =
      isMobileOtpNode(session.next_node_id) || isMobileOtpNode(session.current_node_id)
    const otpVerificationNodeId = isMobileOtpVerificationStep
      ? AbhaJourneyNodeId.MobileOtpVerificationV2
      : AbhaJourneyNodeId.OtpVerificationV2

    try {
      if (isNewAbhaCreateAnalytics) {
        await trackEvent(ABHA_ANALYTICS_EVENTS.TAP_AADHAAR_NUMBER_OTP_SUBMIT, {
          ...getNewAbhaCreateInteractionProps(),
          action_type: isMobileOtpVerificationStep
            ? ABHA_ANALYTICS_ACTION_TYPES.PHONE_OTP_SUBMIT
            : ABHA_ANALYTICS_ACTION_TYPES.AADHAAR_OTP_SUBMIT,
          journey: ABHA_ANALYTICS_JOURNEYS.CREATE_ABHA,
        })
      } else {
        await trackEvent(
          ABHA_ANALYTICS_EVENTS.TAP_BTN_CTA_WIDGET,
          buildAbhaAnalyticsProps({
            journey: ABHA_ANALYTICS_JOURNEYS.NEW_ABHA_ENTRY,
            action_type: isMobileOtpStep
              ? ABHA_ANALYTICS_ACTION_TYPES.PHONE_NUM_OTP_SUBMIT
              : ABHA_ANALYTICS_ACTION_TYPES.AADHAAR_OTP_SUBMIT,
          })
        )
      }

      if (shouldUseMockAbhaFlow(session)) {
        await verifyAbhaAadhaarOtp({ phone: activePhone, aadhaar, otp })
        setStep(AbhaHeroModalStep.Prepare)
        return
      }

      const membersWithOtp = buildMemberListWithOtp(session.abha_member_list_details, otp)
      const updatedMembers = isMobileOtpVerificationStep
        ? buildMemberListWithPhone(membersWithOtp, activePhone)
        : membersWithOtp
      const { session: nextSession } = await advanceAbhaNextStep(session, otpVerificationNodeId, updatedMembers)

      await routeToNextStep(nextSession)
    } catch (error) {
      if (isAbhaOtpInvalidError(error)) {
        setOtpError(error.message)
        return
      }

      if (
        otpVerificationNodeId === AbhaJourneyNodeId.OtpVerificationV2 &&
        error instanceof AbhaFlowError &&
        typeof error.status === 'number' &&
        error.status >= 500
      ) {
        setOtpError(ABHA_HERO_AADHAAR_VERIFICATION_DELAY_ERROR_MESSAGE)
        return
      }

      setOtpError(sanitizeAbhaErrorMessage(error, session))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDownloadApp = () => {
    if (previewMode) return

    if (!isNewAbhaCreateAnalytics) {
      const successData = modalOptions?.successStepData
      const successVariantName = resolveAbhaSuccessVariantName(successData)
      const analyticsPage = segmentEventCommonProperties?.page

      void trackEvent(
        ABHA_ANALYTICS_EVENTS.TAP_BTN_ACKO_APP,
        buildAbhaAnalyticsProps({
          component_name: ABHA_ANALYTICS_JOURNEYS.APP_DOWNLOAD_CLICK,
          page_type: successData?.pageType ?? modalOptions?.pageType ?? segmentEventCommonProperties?.page_type,
          page: typeof analyticsPage === 'string' && analyticsPage ? analyticsPage : window.location.href,
          ...(successVariantName ? { variant_name: successVariantName } : {}),
          current_state: ABHA_STATE.DOWNLOAD_ABHA,
          journey: ABHA_ANALYTICS_JOURNEYS.NEW_ABHA_ENTRY,
        })
      )
    }

    window.location.href = appDownloadLink
  }

  const handleDownloadAbhaCard = () => {
    if (previewMode) {
      try {
        downloadAbhaCardAsset(session, { cardVariant: successCardVariant })
      } catch (error) {
        logger.error('[ABHA card download] Preview download failed', error)
      }

      return
    }

    try {
      if (!isNewAbhaCreateAnalytics) {
        const successData = modalOptions?.successStepData
        const successVariantName = resolveAbhaSuccessVariantName(successData)
        const analyticsPage = segmentEventCommonProperties?.page

        void trackEvent(
          ABHA_ANALYTICS_EVENTS.TAP_BTN_CTA_WIDGET,
          buildAbhaAnalyticsProps({
            journey: ABHA_ANALYTICS_JOURNEYS.NEW_ABHA_ENTRY,
            cta_text: '',
            action_type: ABHA_ANALYTICS_ACTION_TYPES.ABHA_CARD_DOWNLOAD,
            page_type: successData?.pageType ?? modalOptions?.pageType ?? segmentEventCommonProperties?.page_type,
            page: typeof analyticsPage === 'string' && analyticsPage ? analyticsPage : window.location.href,
            variant_name: successVariantName,
            current_state: ABHA_STATE.DOWNLOAD_ABHA,
          })
        )
      }
      downloadAbhaCardAsset(session, { cardVariant: successCardVariant })
    } catch (error) {
      logger.error('[ABHA card download] Download failed', error)
    }
  }

  const handleOtpSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (otp.length !== ABHA_OTP_LENGTH) {
      return
    }

    void handleVerifyOtp()
  }

  const isSendOtpEnabled =
    isValidAadhaarNumber(aadhaar) &&
    (!collectPhoneInAadhaarStep || isValidPhoneNumber(sanitizeAbhaPhoneNumber(modalPhone))) &&
    hasConsent &&
    !isSubmitting
  const isVerifyOtpEnabled = otp.length === ABHA_OTP_LENGTH && !isSubmitting
  const canResendOtp = resendCountdown === 0 && !isSubmitting
  const isFullScreenStep = step === AbhaHeroModalStep.Success || isMobile
  const isDismissible =
    step === AbhaHeroModalStep.Prepare ? false : step === AbhaHeroModalStep.Success ? true : !isMobile
  const dialogSize = isFullScreenStep ? 'full' : 'md'
  const successCardVariant = resolveAbhaSuccessCardVariant(
    modalOptions?.successStepData?.cardVariant ?? DEFAULT_ABHA_SUCCESS_CARD_VARIANT
  )
  const isMobileSuccessHide = isMobile && step === AbhaHeroModalStep.Success && successCardVariant === 'hideAbhaNumber'
  const dialogClassName = getClassNames(['abha-hero-modal'], {
    'abha-hero-modal--prepare': step === AbhaHeroModalStep.Prepare,
    'abha-hero-modal--success': step === AbhaHeroModalStep.Success,
    'abha-hero-modal--mobile-otp': isMobileOtpStep,
    'abha-hero-modal--success-hide': isMobileSuccessHide,
  })

  const renderModalStep = () => {
    switch (step) {
      case AbhaHeroModalStep.Loading:
        return <AbhaHeroPrepareStep data={modalOptions?.prepareStepData} isMobile={isMobile} />

      case AbhaHeroModalStep.Aadhaar:
        return (
          <AbhaHeroAadhaarStep
            data={modalOptions?.aadhaarStepData}
            isMobile={isMobile}
            aadhaar={aadhaar}
            aadhaarError={aadhaarError}
            showPhoneInput={collectPhoneInAadhaarStep}
            phone={modalPhone}
            phoneError={modalPhoneError}
            hasConsent={hasConsent}
            submitError={submitError}
            isSendOtpEnabled={isSendOtpEnabled}
            isSubmitting={isSubmitting}
            onAadhaarChange={handleAadhaarChange}
            onAadhaarBlur={handleAadhaarBlur}
            onPhoneChange={handleModalPhoneChange}
            onPhoneBlur={handleModalPhoneBlur}
            onConsentChange={checked => {
              setHasConsent(checked)
              if (checked) {
                void trackEvent(
                  ABHA_ANALYTICS_EVENTS.TAP_BTN_ENGAGE,
                  buildAbhaAnalyticsProps({
                    journey: ABHA_ANALYTICS_JOURNEYS.NEW_ABHA_ENTRY,
                    component_name: ABHA_ANALYTICS_PAGE_TYPES.AADHAAR_CONSENT,
                  })
                )
              }

              if (submitError) setSubmitError('')
            }}
            onSendOtp={() => {
              void handleSendOtp()
            }}
          />
        )

      case AbhaHeroModalStep.Otp:
        return (
          <AbhaHeroOtpStep
            data={isMobileOtpStep ? modalOptions?.mobileOtpStepData : modalOptions?.otpStepData}
            isMobile={isMobile}
            maskedPhoneNumber={otpMaskedPhoneNumber}
            phoneNumber={getActivePhone()}
            variant={isMobileOtpStep ? 'mobile' : 'aadhaar'}
            otp={otp}
            otpError={otpError}
            submitError={submitError}
            prepareError={prepareError}
            resendCountdown={resendCountdown}
            isVerifyOtpEnabled={isVerifyOtpEnabled}
            isSubmitting={isSubmitting}
            canResendOtp={canResendOtp}
            onOtpChange={value => {
              setOtp(value.replace(/\D/g, '').slice(0, ABHA_OTP_LENGTH))
              if (otpError) setOtpError('')
              if (submitError) setSubmitError('')
            }}
            onResendOtp={() => {
              void handleResendOtp()
            }}
            onSubmit={handleOtpSubmit}
          />
        )

      case AbhaHeroModalStep.Prepare:
        return <AbhaHeroPrepareStep data={modalOptions?.prepareStepData} isMobile={isMobile} />

      case AbhaHeroModalStep.Success:
        return (
          <AbhaHeroSuccessStep
            data={modalOptions?.successStepData}
            isMobile={isMobile}
            appDownloadLink={appDownloadLink}
            cardData={cardPreview}
            onDownloadApp={handleDownloadApp}
            onDownloadAbhaCard={handleDownloadAbhaCard}
            ctaAnalyticsProps={successCtaAnalyticsProps}
          />
        )

      case AbhaHeroModalStep.Error:
        return (
          <AbhaHeroErrorStep
            data={modalOptions?.errorStepData}
            isMobile={isMobile}
            message={errorMessage || submitError}
            onClose={handleClose}
          />
        )

      default:
        return null
    }
  }

  const dialogPanelClassName = getClassNames(['acko-dialog', `acko-dialog-${dialogSize}`, dialogClassName], {})
  const isSuccessStep = step === AbhaHeroModalStep.Success
  const closeButtonSize = isSuccessStep ? 'lg' : 'md'

  const renderModalHeader = () => {
    if (!isDismissible) {
      return null
    }

    return (
      <div className="acko-dialog-header">
        <div className="acko-dialog-heading" aria-hidden />
        <AbhaHeroModalCloseButton onClick={handleClose} size={closeButtonSize} />
      </div>
    )
  }

  const renderDialogPanel = () => (
    <div className={dialogPanelClassName} role="dialog" aria-modal="true" tabIndex={-1}>
      {renderModalHeader()}
      <div className="acko-dialog-body">{renderModalStep()}</div>
    </div>
  )

  if (previewMode) {
    if (!open) return null

    return <div className="abha-hero-modal-preview__stage">{renderDialogPanel()}</div>
  }

  return (
    <Dialog open={open} onClose={handleClose} size={dialogSize} className={dialogClassName} dismissible={false}>
      {renderModalHeader()}
      {renderModalStep()}
    </Dialog>
  )
}
